import Image from "next/image";

import type { AgencyBrandDto } from "@tripcart/shared";
import { cx } from "@tripcart/traveler-ui/cx";

import { resolveAgencyLogo } from "@/lib/brand-logo";

import styles from "./logo.module.css";

interface LogoProps {
  agency: AgencyBrandDto;
  className?: string;
}

/**
 * The agency's brand mark in the site header.
 *
 * Operators upload whatever they have — a 3000px banner, a square app icon, a
 * tall crest — so the size is bounded HERE rather than at each call site. A
 * height alone is not enough: it makes a wide asset proportionally wide and it
 * runs away across the bar, pushing the nav off screen. The box is therefore
 * capped in both axes and `object-contain` letterboxes whatever does not fit,
 * so any aspect ratio lands inside the same slot with its proportions intact.
 *
 * `width: auto` keeps a normal wordmark its natural width; the max-width only
 * bites on the extremes.
 */
export function Logo({ agency, className }: LogoProps) {
  const logo = resolveAgencyLogo(agency);

  // No uploaded logo — the agency's own name is the wordmark, so the header is
  // never empty and never shows the platform's mark on someone else's site.
  // Capped and truncated for the same reason the image is: a long trading name
  // must not push the navigation off screen.
  if (!logo) {
    return (
      <span className={cx(styles.wordmark, className)}>{agency.name}</span>
    );
  }

  return (
    <Image
      src={logo.url}
      alt={logo.alt || agency.name}
      width={logo.width}
      height={logo.height}
      // 40px tall matches the legacy PHP storefront, and the width band comes
      // from the same place: legacy defaulted `site_logo_width` to 120px, and
      // the reference storefronts sit at 112–128px. 176px was mine, and wider
      // than anything real.
      className={cx(styles.image, className)}
      // Eager, not `priority`: the header logo is always above the fold, so
      // lazy-loading it paints the brand late. `priority` would also emit a
      // preload, which competes with the hero image that is the actual LCP
      // element on every storefront page.
      loading="eager"
      placeholder={logo.blurDataURL ? "blur" : "empty"}
      blurDataURL={logo.blurDataURL}
    />
  );
}
